import React, { useState } from "react";
import { Tag, Progress } from "@multiagentbook/ui";
import { FrameworkData, calculateDimensionScore } from "./useFrameworkData";
import { ExternalLink, ChevronDown } from "lucide-react";

interface FrameworkDetailProps {
  framework: FrameworkData;
  isCompareView?: boolean;
}

// Framework logo component that uses imageUrl from framework data
const FrameworkLogo = ({ framework }: { framework: FrameworkData }) => {
  const getInitials = (name: string) => {
    return name
      .split(/\s+/)
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  // Use the imageUrl from framework data if available
  if (framework.imageUrl) {
    return (
      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary/10 overflow-hidden">
        <img
          src={framework.imageUrl}
          alt={framework.framework_name}
          className="w-8 h-8 object-contain"
          onError={(e) => {
            // If image fails to load, show initials instead
            e.currentTarget.style.display = "none";
            e.currentTarget.parentElement!.innerHTML = getInitials(
              framework.framework_name
            );
          }}
        />
      </div>
    );
  }

  // Fallback to initials
  return (
    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary text-accent">
      <span className="text-sm font-bold">
        {getInitials(framework.framework_name)}
      </span>
    </div>
  );
};

const getScoreColor = (score: number) => {
  if (score >= 8) return "#52c41a"; // green
  if (score >= 6.5) return "#1890ff"; // blue
  if (score >= 5) return "#faad14"; // yellow
  return "#f5222d"; // red
};

const FrameworkDetail: React.FC<FrameworkDetailProps> = ({
  framework,
  isCompareView = false,
}) => {
  // State to track which dimension cards are expanded
  const [expandedDimensions, setExpandedDimensions] = useState<{
    [key: string]: boolean;
  }>({});

  // Toggle the expanded state of a dimension
  const toggleDimension = (dimensionSlug: string) => {
    setExpandedDimensions((prev) => ({
      ...prev,
      [dimensionSlug]: !prev[dimensionSlug],
    }));
  };

  // Sort dimensions by score (highest first)
  const sortedDimensions = [...framework.dimensions].sort(
    (a, b) => calculateDimensionScore(b) - calculateDimensionScore(a)
  );

  return (
    <div className={`framework-detail ${isCompareView ? "compare-mode" : ""}`}>
      {/* Header with framework info (only show if not in compare view) */}
      {!isCompareView && (
        <div className="mb-6 flex items-center gap-4">
          <FrameworkLogo framework={framework} />
          <div>
            <h2 className="text-xl font-bold text-primary">
              {framework.framework_name}
            </h2>
            {framework.framework_url && (
              <a
                href={framework.framework_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-accent hover:underline text-sm"
              >
                <ExternalLink size={14} />
                <span>Visit Documentation</span>
              </a>
            )}
          </div>
          <Tag
            color="green"
            className="ml-auto text-base px-3 py-1 font-semibold"
          >
            Overall Score: {framework.avgScore?.toFixed(1)}
          </Tag>
        </div>
      )}

      {/* Summary */}
      <div className="mb-6 p-4 bg-secondary/5 rounded-lg border border-secondary/10">
        <h3 className="text-lg font-semibold mb-2">
          {isCompareView ? "Summary" : "About"}
        </h3>
        <p className="text-secondary">{framework.framework_summary}</p>
      </div>

      {/* Dimensions */}
      <div>
        <h3 className="text-lg font-semibold mb-4">
          {isCompareView ? "Scores by Dimension" : "Framework Dimensions"}
        </h3>

        {sortedDimensions.map((dimension) => (
          <div
            key={dimension.dimension_slug}
            className="mb-6 p-4 bg-tertiary rounded-lg   border-secondary/10 hover:border-secondary/30 transition-all"
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-primary">{dimension.name}</h4>
              <Tag
                color={
                  calculateDimensionScore(dimension) >= 8
                    ? "green"
                    : calculateDimensionScore(dimension) >= 6.5
                    ? "blue"
                    : calculateDimensionScore(dimension) >= 5
                    ? "gold"
                    : "red"
                }
                className="px-2 py-0.5"
              >
                {calculateDimensionScore(dimension).toFixed(1)}
              </Tag>
            </div>

            <div className="mb-3">
              <Progress
                percent={calculateDimensionScore(dimension) * 10}
                strokeColor={getScoreColor(calculateDimensionScore(dimension))}
                showInfo={false}
                strokeWidth={8}
              />
            </div>

            <p className="text-sm text-secondary mb-4">
              {dimension.dimension_justification}
            </p>

            <div>
              <button
                onClick={() => toggleDimension(dimension.dimension_slug)}
                className="flex items-center text-accent hover:text-primary transition-colors gap-1 text-sm font-medium"
              >
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-300 ${
                    expandedDimensions[dimension.dimension_slug]
                      ? "rotate-180"
                      : ""
                  }`}
                />
                <span>View Evidence & Questions</span>
              </button>

              <div
                className={`mt-3 overflow-hidden transition-all duration-300 ease-in-out ${
                  expandedDimensions[dimension.dimension_slug]
                    ? "max-h-[1000px] opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                <h5 className="font-medium text-sm mb-2">Questions</h5>
                <div className="space-y-3">
                  {dimension.questions.map((question) => (
                    <div
                      key={question.question_slug}
                      className="pl-3 border-l-2 border-secondary/20"
                    >
                      <div className="flex justify-between mb-1">
                        <span className="text-sm">{question.question}</span>
                        <Tag color={getScoreColor(question.score)}>
                          {question.score.toFixed(1)}
                        </Tag>
                      </div>

                      {question.evidence && question.evidence.length > 0 && (
                        <div className="text-xs text-secondary bg-secondary/5 p-2 rounded">
                          <span className="font-medium">Evidence: </span>
                          {question.evidence[0].excerpt}
                          {question.evidence[0].source_url && (
                            <a
                              href={question.evidence[0].source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-1 text-accent hover:underline"
                            >
                              [source]
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FrameworkDetail;
